// Past trips (B10 first half): what a trip left behind for a child, without switching the family to
// it: the places they found (checked in at, near), the stickers from that trip's plays, and the
// prizes they won there. Every event names the trip it was made in (D1); events from before trips
// existed belong to the first trip. Pure; the Crate's Past trips sheet shows it.
import type { Pack } from '../../shared/pack/schema'
import { PrizeWon, RETIRED_PRIZES, STARTER_PRIZES } from '../../shared/rewards'

interface Ev {
  id: string
  type: string
  at: number
  personId: string
  tripId?: string
  payload: unknown
}

export interface PastTrip {
  found: { id: string; name: string }[]
  stickers: number
  prizes: { title: string; emoji: string }[]
}

export function pastTrip(events: readonly Ev[], tripId: string, firstTrip: string, personId: string, pack: Pick<Pack, 'places'> | null): PastTrip {
  const mine = events.filter((e) => e.personId === personId && (e.tripId ?? firstTrip) === tripId)
  const foundIds = [...new Set(mine.filter((e) => e.type === 'checkin' && (e.payload as { near?: boolean } | null)?.near).map((e) => (e.payload as { placeId: string }).placeId))]
  const names = new Map((pack?.places ?? []).map((p) => [p.id, p.name]))
  // A prize's title: the family's own edits aren't trip-scoped, so look them up in all events.
  const set = new Map<string, { title?: string; emoji?: string }>()
  for (const e of [...events].filter((x) => x.type === 'prize.set').sort((a, b) => a.at - b.at)) {
    const p = e.payload as { prizeId?: string; fields?: { title?: string; emoji?: string } }
    if (p?.prizeId) set.set(p.prizeId, { ...set.get(p.prizeId), ...p.fields })
  }
  const prizes = mine.flatMap((e) => {
    if (e.type !== 'prize.won') return []
    const w = PrizeWon.safeParse(e.payload)
    if (!w.success) return []
    const base = STARTER_PRIZES[w.data.prizeId] ?? RETIRED_PRIZES[w.data.prizeId]
    const own = set.get(w.data.prizeId)
    return [{ title: own?.title ?? base?.title ?? 'A prize', emoji: own?.emoji ?? base?.emoji ?? '🎁' }]
  })
  return {
    found: foundIds.map((id) => ({ id, name: names.get(id) ?? id })),
    // Old capsule rounds' stickers, and (WI-13) the collectibles that trip's twists paid.
    stickers: mine.filter((e) => e.type === 'session.completed' || (e.type === 'reward.paid' && (e.payload as { kind?: string } | null)?.kind === 'collectible')).length,
    prizes,
  }
}
